'use strict';

// external imports
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';
import _ from 'lodash';
import {v4 as uuidV4} from 'uuid';
import parse from 'html-react-parser';

// prime react imports
import {Button} from 'primereact/button';
import {ContextMenu} from 'primereact/contextmenu';
import {
    MenuItem,
    MenuItemCommandEvent
} from 'primereact/menuitem';
import {Panel} from 'primereact/panel';
import {Tooltip} from 'primereact/tooltip';
import {
    Tree,
    TreeEventNodeEvent,
    TreeSelectionEvent
} from 'primereact/tree';
import {TreeNode as PrimeTreeNode} from 'primereact/treenode';
import {TreeTableExpandedKeysType} from 'primereact/treetable';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributesForCreation
} from '~/shared/src/ci/Item';
import Reference, {
    ReferenceAttributesForCreation
} from '~/shared/src/ci/Reference';
import dataExtraction from '~/shared/src/utilities/dataExtraction';
import {
    DataObjectProperties,
    DataStructure,
    extractDataObjectsFromDataStructure,
    getParentDataObject,
    getReferenceSetsForDataObject
} from '~/shared/src/utilities/dataStructure';
import {TreeView} from '~/shared/src/utilities/modules/treeDesigner';
import {
    DynamicNode,
    TreeNode
} from '~/shared/src/utilities/treeViewNodes';

// frontend components import
import ContentButton, {
    ReferenceType as ContentButtonType
} from '~/frontend/src/components/base/shared/ContentButton';
import getAddItemDialog from '~/frontend/src/components/base/shared/tree/ItemTree/AddTreeNodePanel';
import {
    ReferenceType as TreeDesignerType
} from '~/frontend/src/components/base/TreeDesigner';

// frontend imports
import {resolveLigatureIcon} from '~/frontend/src/utilities/renderHelpers';
import {isInvalidHTML} from '~/frontend/src/utilities/valueRenderers';

// action imports
import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import origSetParams, {SetParamsAction} from '~/frontend/src/actions/panel/common/setParams';
import addItem from '~/frontend/src/actions/data/addItem';
import deleteItem from '~/frontend/src/actions/data/deleteItem';
import updateItem from '~/frontend/src/actions/data/updateItem';
import addReference from '~/frontend/src/actions/data/addReference';
import deleteReference from '~/frontend/src/actions/data/deleteReference';
import updateReference from '~/frontend/src/actions/data/updateReference';


type NodeButtons = {
    [ buttonId : string ] : {
        justInteracted : boolean;
        content? : ContentButtonType;
    }
};
type NodeButtonPanels = {
    [ panelId : string ] : Panel;
};

type ExtendTreeNode = Omit<PrimeTreeNode, 'key'> & {
    key : string;
    label : string;
    deleted : boolean;
    dirty : boolean;
    used : boolean;
    error? : {
        count : number;
        tooltip? : React.ReactNode;
    };
    tooltip? : React.ReactNode;
    dataObject? : DataObjectProperties;
    buttons? : React.ReactNode[];
    children? : ExtendTreeNode[];
};

export type DefaultProps = {

    readOnly? : boolean;

    // props to be passed to ItemTree
    expandedKeys? : TreeTableExpandedKeysType;

    /**
     * Additional context to be updated (elusive data)
     */
    additionalElusiveDataContexts? : string[];

    scrollTop? : number;
    scrollLeft? : number;
};
export const defaultProps : Required<DefaultProps> = {
    readOnly                     : false,
    expandedKeys                 : {},
    additionalElusiveDataContexts: [],
    scrollTop                    : 0,
    scrollLeft                   : 0
};

export type Props = {
    uid : string;
    parentUid : string;

    dataContext : string;

    rootItemId : number;

    highlightedLayoutElementId? : number;

    onHighlightLayoutElement? : ( id? : number ) => void;

    selectedLayoutElementId? : number;

    /**
     * Callback if a layout element is selected.
     *
     * @param id
     */
    onSelectLayoutElement? : ( id? : number ) => void;

    onAddTreeNode? : TreeDesignerType[ 'addTreeNode' ];
    onTreeNodeDragStart? : TreeDesignerType[ 'onDragStart' ];
};
export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

export type AllProps = Required<DefaultProps> & Props;

export type ReferenceType = {};


const setParams = origSetParams as ( panelKey : string, panelProps : Partial<AllProps> ) => SetParamsAction;


const DesignerTree = React.forwardRef<ReferenceType, AllProps>( ( props : AllProps, forwardedRef ) => {
    const dispatch : AppDispatch                    = useDispatch(),
          {
              additionalElusiveDataContexts,
              dataContext,
              expandedKeys,
              highlightedLayoutElementId,
              onAddTreeNode,
              onHighlightLayoutElement,
              onSelectLayoutElement,
              onTreeNodeDragStart,
              parentUid,
              readOnly,
              rootItemId,
              scrollLeft,
              scrollTop,
              selectedLayoutElementId,
              uid
          }                                         = props,
          user                                      = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          messageCatalogTimestamp                   = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          metaModelTimestamp                        = useSelector( ( state : StoreState ) => state.metamodel?.timestamp ? state.metamodel.timestamp : 0 ),
          allElusiveData                            = useSelector( ( state : StoreState ) => state.elusiveData ),
          treeElement                               = useRef<HTMLDivElement | null>( null ),
          contextMenu                               = useRef<ContextMenu | null>( null ),
          [ contextMenuModel, setContextMenuModel ] = useState<MenuItem[]>( [] ),
          nodeButtons                               = useRef<NodeButtons>( {} ),
          nodeButtonPanels                          = useRef<NodeButtonPanels>( {} ),
          ignoreSelectionChange                     = useRef<boolean>( false ),
          buildContextMenuRef                       = useRef<typeof buildContextMenu>( null! ),
          [ contextMenuNode, setContextMenuNode ]   = useState<string | undefined>( undefined ),
          language                                  = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          globalDataVersions                        = useMemo(
              () => {
                  return JSON.stringify( allElusiveData?.[ dataContext ]?.data ?? {} );
              },
              [
                  allElusiveData?.[ dataContext ]?.data
              ]
          ),
          staticTreeNodes : ExtendTreeNode[]        = useMemo(
              () => {
                  return [
                      {
                          id        : 'TreeNode',
                          key       : 'TreeNode',
                          label     : __( 'YN.base.frontend.treeDesigner.node.treeNode.label' ),
                          deleted   : false,
                          dirty     : false,
                          used      : false,
                          selectable: false,
                          className : 'yn-designertree-node',
                          icon      : resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${metaModel[ 'CFG' ].getItemType( 'TreeNode' )?.icon}` )
                      }
                  ];
              },
              [
                  language,
                  messageCatalogTimestamp,
                  metaModelTimestamp
              ]
          ),
          rootItem                                  = useMemo(
              () => {
                  return global.data[ dataContext ]?.items[ rootItemId ];
              },
              [
                  dataContext,
                  rootItemId,
                  globalDataVersions
              ]
          ),
          treeView                                  = useMemo(
              () => {
                  let retVal : TreeView | undefined = undefined;

                  if ( rootItem ) {
                      retVal = dataExtraction.extractTreeViewData( rootItem, dataContext, true );
                  }

                  return retVal;
              },
              [
                  dataContext,
                  globalDataVersions,
                  rootItem
              ]
          ),
          flatTreeNodes                             = useMemo(
              () => {
                  const retVal : ( DynamicNode | TreeNode )[] = [],
                        addRecursive                          = ( nodes : ( DynamicNode | TreeNode )[] ) => {
                            for ( const node of nodes ) {
                                if ( !retVal.find( n => n.data.id === node.data.id ) ) {
                                    retVal.push( node );
                                }

                                if ( node.nodes ) {
                                    addRecursive( node.nodes );
                                }
                            }
                        };

                  if ( treeView?.nodes ) {
                      addRecursive( treeView.nodes );
                  }

                  return retVal;
              },
              [
                  treeView?.nodes
              ]
          ),
          dataObjects                               = useMemo(
              () => {
                  let retVal : DataObjectProperties[] = [];

                  if ( treeView?.dataStructure ) {
                      retVal = extractDataObjectsFromDataStructure( treeView.dataStructure );
                  }

                  return retVal;
              },
              [
                  treeView?.dataStructure
              ]
          ),
          dataObjectsWithErrors                     = useMemo(
              () => {
                  const retVal : number[] = [];

                  if ( dataObjects.length ) {
                      for ( const dataObject of dataObjects ) {
                          if ( allElusiveData?.[ `${parentUid}_form-${dataObject.origId}` ]?.errorData?.count ) {
                              retVal.push( dataObject.origId );
                          }
                      }
                  }

                  return retVal;
              },
              [
                  dataObjects,
                  allElusiveData
              ]
          ),
          addNewNode                                = useCallback(
              ( dataObjectToUse : DataObjectProperties, dataStructureToUse : DataStructure, item : ItemAttributesForCreation | Item, reference : ReferenceAttributesForCreation ) => {
                  const dataObjectSchema       = ( dataObjectToUse.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                        dataObjectItemType     = metaModel[ dataObjectSchema ].getItemType( item.itemTypeId )?.name,
                        dataObjectReferenceSet = metaModel[ dataObjectSchema ].getReferenceSet( reference.referenceSetId );

                  if ( dataStructureToUse && dataObjectItemType && dataObjectReferenceSet ) {
                      const activeStatusId                      = metaModel[ 'CFG' ].getStatus( 'active' )!.id,
                            dataObjectItemTypeId                = metaModel[ 'CFG' ].getItemType( 'DataObject' )!.id,
                            objectTypePropertySetId             = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectType' )!.id,
                            objectSchemaPropertySetId           = metaModel[ 'CFG' ].getPropertySetForItemTypeAndPropertyType( dataObjectItemTypeId, 'ObjectSchema' )!.id,
                            isReferencedToReferenceSetId        = metaModel[ 'CFG' ].getReferenceSetsForItemTypesAndReferenceType( dataObjectItemTypeId, dataObjectItemTypeId, 'isReferencedTo', 'dest' )![ 0 ].id,
                            referenceConfigurationPropertySetId = metaModel[ 'CFG' ].getReferencePropertySetForReferenceSetAndPropertyType( isReferencedToReferenceSetId, 'ReferenceConfiguration' )!.id;

                      let referenceDirection : 'SrcToDest' | 'DestToSrc' = 'DestToSrc';

                      if (
                          (
                              dataObjectReferenceSet.srcItemType.name === dataObjectReferenceSet.destItemType.name
                              && reference.srcItemId
                          )
                          || dataObjectReferenceSet.srcItemType.name === dataObjectItemType
                      ) {
                          referenceDirection = 'SrcToDest';
                      }

                      // recursive
                      if ( dataObjectToUse.item.objectType === dataObjectItemType ) {
                          dispatch( addReference(
                              dataContext,
                              {
                                  referenceSetId: isReferencedToReferenceSetId,
                                  statusId      : activeStatusId,
                                  srcItemId     : dataObjectToUse.origId,
                                  destItemId    : dataObjectToUse.origId,
                                  enumProperties: [
                                      {
                                          referenceId           : Reference.uniqueIdCount,
                                          referencePropertySetId: referenceConfigurationPropertySetId,
                                          properties            : [
                                              {
                                                  referenceId           : Reference.uniqueIdCount,
                                                  referencePropertySetId: referenceConfigurationPropertySetId,
                                                  key                   : dataObjectReferenceSet?.referenceType.name,
                                                  value                 : `{ "referenceDirection": "${referenceDirection}" }`,
                                                  statusId              : activeStatusId
                                              }
                                          ]
                                      }
                                  ]
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          // select element
                          onSelectLayoutElement?.( dataObjectToUse.origId );
                      }
                      else {
                          const newItem = dispatch( addItem(
                              dataContext,
                              {
                                  statusId  : activeStatusId,
                                  itemTypeId: dataObjectItemTypeId,
                                  key       : `${rootItem.key}:${dataObjectItemType}:${uuidV4().substring( 0, 8 )}`,
                                  name      : dataObjectItemType,
                                  properties: [
                                      {
                                          propertySetId: objectTypePropertySetId,
                                          value        : dataObjectItemType,
                                          statusId     : activeStatusId
                                      },
                                      {
                                          propertySetId: objectSchemaPropertySetId,
                                          value        : dataObjectSchema.toLowerCase(),
                                          statusId     : activeStatusId
                                      }
                                  ]
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          const parentReference = dispatch( addReference(
                              dataContext,
                              {
                                  referenceSetId: isReferencedToReferenceSetId,
                                  statusId      : activeStatusId,
                                  srcItemId     : newItem.id,
                                  destItemId    : dataObjectToUse.origId,
                                  enumProperties: [
                                      {
                                          referenceId           : Reference.uniqueIdCount,
                                          referencePropertySetId: referenceConfigurationPropertySetId,
                                          properties            : [
                                              {
                                                  referenceId           : Reference.uniqueIdCount,
                                                  referencePropertySetId: referenceConfigurationPropertySetId,
                                                  key                   : dataObjectReferenceSet?.referenceType.name,
                                                  value                 : `{ "referenceDirection": "${referenceDirection}" }`,
                                                  statusId              : activeStatusId
                                              }
                                          ]
                                      }
                                  ]
                              },
                              'CFG',
                              additionalElusiveDataContexts
                          ) );

                          if ( parentReference ) {
                              newItem.registerReference( parentReference );
                          }

                          // select new element
                          onSelectLayoutElement?.( newItem.id );
                      }
                  }
              },
              [
                  additionalElusiveDataContexts,
                  dataContext,
                  onSelectLayoutElement,
                  rootItem,
                  uid
              ]
          ),
          deleteOrUndeleteNode                      = ( dataObjectToUse : DataObjectProperties ) => {
              const dataObjectItem              = global.data[ dataContext ].items[ dataObjectToUse.origId ],
                    dataObjectItemDeleted       = dataObjectItem.isDeleted(),
                    referenceToParentDataObject = global.data[ dataContext ]?.references?.[ dataObjectItem.references.find(
                        ( referenceId ) => {
                            const reference = global.data[ dataContext ]?.references?.[ referenceId ];

                            let retVal : boolean = false;

                            if (
                                reference
                                && reference.srcItemId === dataObjectItem.id
                                && reference.referenceSet.referenceType.name === 'isReferencedTo'
                            ) {
                                retVal = true
                            }

                            return retVal;
                        }
                    ) ?? 0 ];

              // undelete
              if ( dataObjectItemDeleted ) {
                  if ( referenceToParentDataObject ) {
                      dispatch( updateReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          { statusId: referenceToParentDataObject.attributesModified.get( 'statusId' ) as number },
                          additionalElusiveDataContexts
                      ) );
                  }

                  dispatch( updateItem(
                      dataContext,
                      dataObjectItem.id,
                      { statusId: dataObjectItem.attributesModified.get( 'statusId' ) as number },
                      additionalElusiveDataContexts
                  ) );
              }

              // delete
              else {
                  if ( referenceToParentDataObject ) {
                      dispatch( deleteReference(
                          dataContext,
                          referenceToParentDataObject.id,
                          true,
                          additionalElusiveDataContexts,
                          false
                      ) );
                  }

                  dispatch( deleteItem(
                      dataContext,
                      dataObjectItem.id,
                      true,
                      additionalElusiveDataContexts,
                      false,
                      false
                  ) );

                  // deselect if selected
                  if ( selectedLayoutElementId === dataObjectToUse.origId ) {
                      onSelectLayoutElement?.();
                  }

                  onHighlightLayoutElement?.();
              }
          },
          buildContextMenu                          = ( nodeId : string, showOnly : false | 'add' | 'delete' = false, dataContextToUse? : string, dataStructureToUse? : DataStructure, readOnlyToUse? : boolean, flatTreeNodesToUse? : ( TreeNode | DynamicNode )[] ) => {
              dataContextToUse = dataContextToUse ?? dataContext;
              dataStructureToUse = dataStructureToUse ?? treeView?.dataStructure;
              readOnlyToUse = readOnlyToUse ?? readOnly;
              flatTreeNodesToUse = flatTreeNodesToUse ?? flatTreeNodes;

              const retVal : MenuItem[] = [];

              if ( !readOnlyToUse && dataContextToUse && dataStructureToUse ) {
                  const dataObject = extractDataObjectsFromDataStructure( dataStructureToUse ).find( dS => dS.id === nodeId ) as DataStructure | undefined;

                  if ( dataObject ) {
                      const dataObjectItem = global.data[ dataContextToUse ]?.items[ dataObject.origId ];

                      if ( !dataObjectItem ) {
                          return retVal;
                      }

                      const parentDataObject      = getParentDataObject( dataObject.id, dataStructureToUse ),
                            dataObjectItemDeleted = dataObjectItem.isDeleted(),
                            dataObjectSchema      = ( dataObject.item.objectSchema ?? dataStructureToUse.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                            dataObjectItemType    = metaModel[ dataObjectSchema ].getItemType( dataObject.item.objectType ?? '' ),
                            inUse                 = !!flatTreeNodesToUse.find( n => ( ( n as DynamicNode ).dataObjectIds ?? [] ).includes( dataObject.origId ) );

                      let addButtonContent : ( () => any ) | undefined = undefined;

                      if ( dataObjectItemType ) {

                          // TODO: handle circular referenceSets

                          const allowedReferenceSets = metaModel[ dataObjectSchema ].getReferenceSetsForItemType( dataObjectItemType.id ),
                                definedReferenceSets = [
                                    ...(
                                        parentDataObject
                                            ? getReferenceSetsForDataObject( dataObject, dataStructureToUse, dataObjectSchema )
                                            : []
                                    ),
                                    ...(
                                        dataObject.dataStructure
                                            ? dataObject.dataStructure.flatMap( childDataStructureToUse => getReferenceSetsForDataObject( childDataStructureToUse, dataStructureToUse!, dataObjectSchema ) )
                                            : []
                                    )
                                ];

                          if ( allowedReferenceSets.length - definedReferenceSets.length > 0 ) {
                              const includeReferenceSetIds = allowedReferenceSets.reduce(
                                        ( innerRetVal, rSet ) => {
                                            if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                                innerRetVal.push( rSet.id );
                                            }

                                            return innerRetVal;
                                        },
                                        [] as number[]
                                    ),
                                    allowedReferenceSetIds = allowedReferenceSets.reduce(
                                        ( innerRetVal, rSet ) => {
                                            if ( !definedReferenceSets.find( dRSet => dRSet.id === rSet.id ) ) {
                                                innerRetVal[ rSet.id ] = rSet.destItemType.id === dataObjectItemType.id ? 'dest' : 'src';
                                            }

                                            return innerRetVal;
                                        },
                                        {} as { [ referenceSetId : number ] : 'dest' | 'src' }
                                    );

                              addButtonContent = () => {
                                  return getAddItemDialog( {
                                      uid                   : uid + '-AddItemReferenceDialog_button_content',
                                      dataContext           : dataContextToUse!,
                                      schema                : dataObjectSchema,
                                      includeReferenceSets  : includeReferenceSetIds,
                                      allowedReferenceSetIds: allowedReferenceSetIds,
                                      targetItemId          : dataObject.origId,
                                      addMode               : 'new',
                                      rawMode               : false,
                                      showAdditionalRawNames: false,
                                      useThisLabel          : false,
                                      onAddNode             : addNewNode.bind( null, dataObject, dataStructureToUse! ),
                                      onHide                : () => {
                                          nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                      },
                                      onInteraction         : () => {
                                          nodeButtons.current[ buttonId ].justInteracted = true;
                                          ignoreSelectionChange.current = true;
                                      }
                                  } );
                              };
                          }
                      }

                      let buttonId : string,
                          menuItemId : string;

                      // add button
                      if ( showOnly !== 'delete' && !dataObjectItemDeleted ) {
                          buttonId = `${uid}_${dataObject.id}_addButton`;
                          menuItemId = `${dataObject.id}_addItem`;

                          retVal.push( {
                              id     : menuItemId,
                              label  : __( 'YN.base.frontend.dataStructureTree.dataObject.add.label' ),
                              icon   : 'pi pi-plus-circle yn-add',
                              data   : {
                                  buttonId,
                                  menuItemId,
                                  content  : addButtonContent,
                                  noConfirm: true
                              },
                              command: ( e : MenuItemCommandEvent ) => {
                                  const event = e.originalEvent as React.MouseEvent;

                                  nodeButtons.current?.[ buttonId! ].content?.expand( event );
                              }
                          } );
                      }

                      // delete/restore button
                      if ( showOnly !== 'add' && !inUse && parentDataObject ) {
                          buttonId = `${uid}_${dataObject.id}_deleteButton`;

                          if ( retVal.length ) {
                              retVal.push( { separator: true } );
                          }

                          // undelete option
                          if ( dataObjectItemDeleted ) {
                              menuItemId = `${dataObject.id}_undeleteItem`;
                              retVal.push( {
                                  id     : menuItemId,
                                  label  : __( `YN.base.frontend.dataStructureTree.dataObject.undelete.label` ),
                                  icon   : 'pi pi-plus-circle yn-dirty',
                                  data   : {
                                      buttonId,
                                      menuItemId,
                                      content: <Panel
                                          key={menuItemId}
                                          id={`${menuItemId}_panel`}

                                          style={{ width: '25rem' }}
                                          className="yn-confirm-option-panel"
                                      >
                                          <label>
                                              <span className="pi pi-question-circle yn-blue"></span>
                                              {__( `YN.base.designer.tree.item.undelete.text` )}
                                          </label>
                                          <Button
                                              label={__( 'YN.base.common.no' )}
                                              className="p-button-sm p-button-secondary mr-3"
                                              rounded={false}
                                              text={false}
                                              outlined={true}
                                              onClick={( event ) => {
                                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                              }}
                                          />
                                          <Button
                                              label={__( 'YN.base.common.yes' )}
                                              className="p-button-sm p-confirm-popup-accept"
                                              rounded={false}
                                              text={false}
                                              outlined={true}
                                              onClick={( event ) => {
                                                  deleteOrUndeleteNode( dataObject );

                                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                              }}
                                          />
                                      </Panel>
                                  },
                                  command: ( e : MenuItemCommandEvent ) => {
                                      const event = e.originalEvent as React.MouseEvent;

                                      if ( !event.altKey ) {
                                          nodeButtons.current?.[ buttonId! ].content?.expand( event );
                                      }
                                      else {
                                          deleteOrUndeleteNode( dataObject );
                                      }
                                  }
                              } );
                          }
                          else {
                              menuItemId = `${dataObject.id}_deleteItem`;

                              retVal.push( {
                                  id     : menuItemId,
                                  label  : __( `YN.base.frontend.dataStructureTree.dataObject.delete.label` ),
                                  icon   : 'pi pi-minus-circle yn-delete',
                                  data   : {
                                      buttonId,
                                      menuItemId,
                                      content: <Panel
                                          key={menuItemId}
                                          id={`${menuItemId}_panel`}

                                          style={{ width: '25rem' }}
                                          className="yn-confirm-option-panel"
                                      >
                                          <label>
                                              <span className="pi pi-question-circle yn-blue"></span>
                                              {__( 'YN.base.frontend.dataStructureTree.dataObject.delete.confirmQuestion', { itemKey: dataObjectItem.key, itemName: dataObjectItem.name, itemDisplayAttributeValue: dataObjectItem.displayAttributeValue } )}
                                          </label>
                                          <Button
                                              label={__( 'YN.base.common.no' )}
                                              className="p-button-sm p-button-secondary mr-3"
                                              rounded={false}
                                              text={false}
                                              outlined={true}
                                              onClick={( event ) => {
                                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                              }}
                                          />
                                          <Button
                                              label={__( 'YN.base.common.yes' )}
                                              className="p-button-sm p-confirm-popup-accept"
                                              rounded={false}
                                              text={false}
                                              outlined={true}
                                              onClick={( event ) => {
                                                  deleteOrUndeleteNode( dataObject );

                                                  nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                              }}
                                          />
                                      </Panel>
                                  },
                                  command: ( e : MenuItemCommandEvent ) => {
                                      const event = e.originalEvent as React.MouseEvent;

                                      if ( !event.altKey ) {
                                          nodeButtons.current?.[ buttonId! ].content?.expand( event );

                                          /*if ( nodeButtonPanels.current[ menuItemId ] ) {
                                              setLayoutCallback( () => {
                                                  nodeButtonPanels.current[ menuItemId ].expand( e.originalEvent as unknown as React.MouseEvent );
                                              } );
                                          }*/
                                      }
                                      else {
                                          deleteOrUndeleteNode( dataObject );
                                      }
                                  }
                              } );
                          }
                      }
                  }
              }

              return retVal;
          },
          buildTreeNodeButton                       = ( nodeId : string, type : 'add' | 'delete' ) => {
              const contentMenuItems = buildContextMenu( nodeId, type );

              let retVal : React.ReactElement,
                  buttonId : string;

              if ( contentMenuItems.length === 1 ) {
                  const getContent = () => {
                      if ( typeof contentMenuItems[ 0 ].data.content === 'function' ) {
                          return contentMenuItems[ 0 ].data.content();
                      }
                      else {
                          return contentMenuItems[ 0 ].data.content;
                      }
                  };

                  buttonId = contentMenuItems[ 0 ].data.buttonId!;

                  nodeButtons.current[ buttonId ] = {
                      justInteracted: false
                  };

                  retVal = <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: el
                              };
                          }
                      }}

                      icon={contentMenuItems[ 0 ].icon}
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      onExpand={( event ) => {
                          const innerRetVal = !!getContent();

                          ignoreSelectionChange.current = innerRetVal;

                          if ( !contentMenuItems[ 0 ].data.noConfirm && ( event.altKey || !innerRetVal ) ) {
                              contentMenuItems[ 0 ].command?.( {
                                  originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                                  item         : contentMenuItems[ 0 ]
                              } );
                          }

                          return innerRetVal;
                      }}

                      onCollapse={( _event, isOutsideClicked ) => {
                          const innerRetVal = !nodeButtons.current[ buttonId ].justInteracted && isOutsideClicked;

                          nodeButtons.current[ buttonId ].justInteracted = false;

                          ignoreSelectionChange.current = true;

                          return innerRetVal;
                      }}

                      content={getContent}
                  />;
              }
              else if ( contentMenuItems.length ) {
                  buttonId = contentMenuItems[ 0 ].data.buttonId!;

                  nodeButtons.current[ buttonId ] = {
                      justInteracted: false
                  };

                  const buttonContent : React.ReactNode[] = [];

                  for ( let i = 0; i < contentMenuItems.length; i++ ) {
                      const menuItemId = contentMenuItems[ i ].data.menuItemId!,
                            content    = typeof contentMenuItems[ i ].data.content === 'function'
                                ? contentMenuItems[ i ].data.content()
                                : contentMenuItems[ i ].data.content;

                      delete nodeButtonPanels.current[ menuItemId ];

                      buttonContent.push(
                          <Panel
                              key={menuItemId}
                              id={`${menuItemId}_panel`}

                              ref={el => {
                                  if ( el ) {
                                      nodeButtonPanels.current[ menuItemId ] = el;
                                  }
                              }}

                              header={
                                  <Button
                                      label={contentMenuItems[ i ].label}
                                      icon={contentMenuItems[ i ].icon}
                                      rounded={false}
                                      text={true}
                                      className="yn-menu-button yn-no-rounded-borders"
                                      onClick={( event ) => {
                                          if ( !contentMenuItems[ i ].data.noConfirm && ( event.altKey || !contentMenuItems[ i ].data.content ) ) {
                                              contentMenuItems[ i ].command?.( {
                                                  originalEvent: new MouseEvent( 'click', { altKey: event.altKey } ) as unknown as React.MouseEvent,
                                                  item         : contentMenuItems[ i ]
                                              } );

                                              nodeButtons.current[ buttonId ].content?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                          }
                                          else {
                                              for ( const dPanelId of Object.keys( nodeButtonPanels.current ) ) {
                                                  if ( dPanelId !== menuItemId ) {
                                                      nodeButtonPanels.current[ dPanelId ].collapse( event );
                                                  }
                                                  else {
                                                      nodeButtonPanels.current[ dPanelId ].toggle( event );
                                                  }
                                              }
                                          }
                                      }}
                                  />
                              }

                              toggleable={true}
                              collapsed={true}
                              className="yn-confirm-option-panel"
                              pt={{
                                  root: {
                                      style: {
                                          padding: '0'
                                      }
                                  }
                              }}
                          >
                              {content}
                          </Panel>
                      );
                  }

                  retVal = <ContentButton
                      key={buttonId}
                      id={buttonId}

                      ref={el => {
                          if ( el ) {
                              nodeButtons.current[ buttonId ] = {
                                  ...nodeButtons.current[ buttonId ],
                                  content: el
                              };
                          }
                      }}

                      icon={contentMenuItems[ 0 ].icon}
                      rounded={true}
                      text={true}
                      horizontalContentAlign="left"
                      verticalContentAlign="bottom"

                      content={
                          <Panel
                              style={{ width: '25rem' }}
                              pt={{
                                  content: {
                                      style: {
                                          border : 'none',
                                          padding: '.5rem 1rem'
                                      }
                                  }
                              }}
                          >
                              {buttonContent}
                          </Panel>
                      }
                  />;
              }
              else {
                  return null;
                  /*return <Button
                      key={`${uid}_${nodeId}_${type}Button`}
                      id={`${uid}_${nodeId}_${type}Button`}

                      icon={`pi ${type === 'delete' ? 'pi-minus-circle' : 'pi-plus-circle'} yn-deleted`}
                      rounded={true}
                      text={true}
                      disabled={true}
                  />;*/
              }

              return retVal;
          },
          buildNodeData                             = ( dataObjectToUse : DataStructure, schema? : Schema ) : ExtendTreeNode | undefined => {
              const dataObjectItem = global.data[ dataContext ]?.items[ dataObjectToUse.origId ];

              if ( !dataObjectItem || !treeView?.dataStructure ) {
                  return undefined;
              }

              const parentDataObject              = getParentDataObject( dataObjectToUse.id, treeView.dataStructure ),
                    dataObjectItemDeleted         = dataObjectItem.isDeleted(),
                    dataObjectKey                 = dataObjectToUse.id,
                    dataObjectSchema              = ( schema ?? dataObjectToUse.item.objectSchema ?? treeView.dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                    dataObjectItemTypeNameRaw     = dataObjectToUse.item.objectType ?? '',
                    excludeDataObjectItemType     = dataObjectItemTypeNameRaw.startsWith( '!' ),
                    dataObjectItemTypeName        = excludeDataObjectItemType
                        ? dataObjectItemTypeNameRaw.substring( 1 )
                        : dataObjectItemTypeNameRaw,
                    dataObjectItemType            = metaModel[ dataObjectSchema ].getItemType( dataObjectItemTypeName ),
                    dataObjectReferenceSets       = parentDataObject
                        ? getReferenceSetsForDataObject( dataObjectToUse, treeView.dataStructure, dataObjectSchema )
                        : undefined,
                    inUse                         = !!flatTreeNodes.find( n => ( ( n as DynamicNode ).dataObjectIds ?? [] ).includes( dataObjectToUse.origId ) ),
                    processedRefLabels : string[] = [];


              let retVal : ExtendTreeNode | undefined                  = undefined,
                  label                                                = dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? '',
                  tooltip : ( string | React.ReactNode )[] | undefined = undefined;

              if ( dataObjectItemType ) {
                  // special label for referenced Message or Image item
                  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                  if ( ( dataObjectItemType.name === 'Message' || dataObjectItemType.name === 'Image' ) && dataObjectReferenceSets?.length === 1 ) {
                      label = `${_.upperFirst( dataExtraction.extractIsForReferenceTypeName( dataObjectReferenceSets[ 0 ]?.referenceTypeLabel ?? '' ) )} (${dataObjectItemType.label})`;
                  }
                  else if (
                      dataObjectToUse !== treeView.dataStructure
                      && dataObjectToUse.item.name !== dataObjectItemType.name
                      && dataObjectToUse.item.name.substring( 1 ) !== dataObjectItemType.name
                  ) {
                      label = `${dataObjectToUse.item.name ? ` ${dataObjectToUse.item.name} ( ` : ''}${dataObjectItemType.label}${dataObjectToUse.item.name ? ` )` : ''}`
                  }
                  // mark self referencing DataObject
                  if ( parentDataObject?.origId === dataObjectToUse.origId ) {
                      label += ` <span class="fas fa-redo yn-danger"></span>`;
                  }

                  // tooltip
                  if ( parentDataObject ) {
                      let tooltipKeyCount = 0;

                      tooltip = [];

                      if ( dataObjectToUse.reference?.referenceConfiguration ) {
                          for ( const referenceTypeName of Object.keys( dataObjectToUse.reference.referenceConfiguration ) ) {
                              const referenceDirection = dataObjectToUse.reference.referenceConfiguration[ referenceTypeName ].referenceDirection;

                              if ( tooltip.length ) {
                                  tooltip.push( <br key={`tK_${tooltipKeyCount++}`}/> );
                              }

                              if ( parentDataObject?.origId === dataObjectToUse.origId ) {
                                  const refLabel = `${referenceTypeName}<->`;

                                  if ( !processedRefLabels.includes( refLabel ) ) {
                                      processedRefLabels.push( refLabel );

                                      tooltip.push(
                                          <div key={`tK_${tooltipKeyCount++}`}>
                                              <div className="yn-tooltip-header">
                                                  <div className="fas fa-redo yn-danger"/>
                                                  &nbsp;&nbsp;
                                                  {referenceTypeName}
                                              </div>
                                          </div>
                                      );
                                  }
                              }
                              else if ( referenceDirection === 'SrcToDest' ) {
                                  const refLabel = `${referenceTypeName}<-${dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? ''}`;

                                  processedRefLabels.push( refLabel );

                                  tooltip.push(
                                      <div key={`tK_${tooltipKeyCount++}`}>
                                          <div className="yn-tooltip-header">
                                              {__( 'YN.base.common.reference.directionMarkerReversed' )}
                                              &nbsp;
                                              {referenceTypeName}
                                              &nbsp;
                                              {__( 'YN.base.common.reference.directionMarkerReversed' )}
                                              &nbsp;
                                              {excludeDataObjectItemType &&
                                                  <>
                                                      <div className="fas fa-not-equal yn-secondary"/>
                                                      &nbsp;</>
                                              }
                                              {dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? ''}
                                          </div>
                                      </div>
                                  );
                              }
                              else if ( referenceDirection === 'DestToSrc' ) {
                                  const refLabel = `${referenceTypeName}->${dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? ''}`;

                                  processedRefLabels.push( refLabel );
                                  tooltip.push(
                                      <div key={`tK_${tooltipKeyCount++}`}>
                                          <div className="yn-tooltip-header">
                                              {__( 'YN.base.common.reference.directionMarker' )}
                                              &nbsp;
                                              {referenceTypeName}
                                              &nbsp;
                                              {__( 'YN.base.common.reference.directionMarker' )}
                                              &nbsp;
                                              {excludeDataObjectItemType &&
                                                  <>
                                                      <div className="fas fa-not-equal yn-secondary"/>
                                                      &nbsp;</>
                                              }
                                              {dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? ''}
                                          </div>
                                      </div>
                                  );
                              }
                              else {
                                  const refLabel = `${referenceTypeName}-${dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? ''}`;

                                  processedRefLabels.push( refLabel );

                                  tooltip.push(
                                      <div key={`tK_${tooltipKeyCount++}`}>
                                          <div className="yn-tooltip-header">
                                              -
                                              &nbsp;
                                              {referenceTypeName}
                                              &nbsp;
                                              -
                                              &nbsp;
                                              {excludeDataObjectItemType &&
                                                  <>
                                                      <div className="fas fa-not-equal yn-secondary"/>
                                                      &nbsp;</>
                                              }
                                              {dataObjectItemType?.label ?? dataObjectToUse.item.objectType ?? ''}
                                          </div>
                                      </div>
                                  );
                              }
                          }
                      }

                      for ( const dataObjectReferenceSet of dataObjectReferenceSets ?? [] ) {
                          if ( parentDataObject?.origId === dataObjectToUse.origId ) {
                              const refLabel = `${dataObjectReferenceSet.referenceTypeLabel}<->`;

                              if ( !processedRefLabels.includes( refLabel ) ) {
                                  processedRefLabels.push( refLabel );

                                  tooltip.push(
                                      <div key={`tK_${tooltipKeyCount++}`}>
                                          <div className="fas fa-redo yn-danger"/>
                                          &nbsp;&nbsp;
                                          {dataObjectReferenceSet.referenceTypeLabel}
                                          &nbsp;&nbsp;
                                          <div className="fas fa-redo yn-danger"/>
                                      </div>
                                  );
                              }
                          }
                          else if ( dataObjectReferenceSet.destItemTypeId === dataObjectItemType.id ) {
                              const refLabel = `${dataObjectReferenceSet.referenceTypeLabel}<-${dataObjectReferenceSet.srcItemType.label}`;

                              if ( !processedRefLabels.includes( refLabel ) ) {
                                  processedRefLabels.push( refLabel );

                                  tooltip.push(
                                      <div key={`tK_${tooltipKeyCount++}`}>
                                          {__( 'YN.base.common.reference.directionMarkerReversed' )}
                                          &nbsp;
                                          {dataObjectReferenceSet.referenceTypeLabel}
                                          &nbsp;
                                          {__( 'YN.base.common.reference.directionMarkerReversed' )}
                                          &nbsp;
                                          {dataObjectReferenceSet.srcItemType.label}
                                      </div>
                                  );
                              }
                          }
                          else {
                              const refLabel = `${dataObjectReferenceSet.referenceTypeLabel}->${dataObjectReferenceSet.destItemType.label}`;

                              if ( !processedRefLabels.includes( refLabel ) ) {
                                  processedRefLabels.push( refLabel );

                                  tooltip.push(
                                      <div key={`tK_${tooltipKeyCount++}`}>
                                          {__( 'YN.base.common.reference.directionMarker' )}
                                          &nbsp;
                                          {dataObjectReferenceSet.referenceTypeLabel}
                                          &nbsp;
                                          {__( 'YN.base.common.reference.directionMarker' )}
                                          &nbsp;
                                          {dataObjectReferenceSet.destItemType.label}
                                      </div>
                                  );
                              }
                          }
                      }
                  }
              }

              retVal = {
                  id        : dataObjectKey,
                  key       : dataObjectKey,
                  dataObject: dataObjectToUse,
                  label,
                  tooltip,
                  deleted   : dataObjectItemDeleted,
                  dirty     : dataObjectItem.isModified(),
                  used      : inUse,
                  error     : allElusiveData?.[ `${parentUid}_form-${dataObjectToUse.origId}` ]?.errorData,
                  selectable: !dataObjectItemDeleted,
                  className : 'yn-designertree-node',
                  icon      : resolveLigatureIcon( `yn-tree-icon icon-itemtype-detail ${!excludeDataObjectItemType ? ( dataObjectItemType?.icon ?? '' ) : 'fas fa-not-equal yn-secondary'}` ),
                  buttons   : [
                      buildTreeNodeButton( dataObjectKey, 'add' ),
                      buildTreeNodeButton( dataObjectKey, 'delete' )
                  ]
              };

              return retVal;
          },
          buildTreeData                             = () : ExtendTreeNode[] => {
              const retVal : ExtendTreeNode[] = [ ...staticTreeNodes ];

              if ( treeView?.dataStructure ) {
                  const schema                 = ( treeView.dataStructure.item.objectSchema ?? 'data' ).toUpperCase() as Schema,
                        buildNodeDataRecursive = ( dataStructureToProcess : DataStructure ) => {
                            const nodeData = buildNodeData( dataStructureToProcess, schema );

                            if ( nodeData ) {
                                // process children only if not deleted
                                if ( !global.data[ dataContext ].items[ dataStructureToProcess.origId ].isDeleted() && dataStructureToProcess.dataStructure?.length ) {
                                    nodeData.children = [];

                                    for ( const childDataStructureToProcess of dataStructureToProcess.dataStructure ) {
                                        const childNodeData = buildNodeDataRecursive( childDataStructureToProcess );

                                        if ( childNodeData ) {
                                            nodeData.children.push( childNodeData );
                                        }
                                    }

                                    // sort by label
                                    nodeData.children.sort( ( childNodeData1, childNodeData2 ) => {
                                        if ( ( childNodeData1.label ?? '' ) > ( childNodeData2.label ?? '' ) ) {
                                            return 1;
                                        }
                                        else if ( ( childNodeData2.label ?? '' ) > ( childNodeData1.label ?? '' ) ) {
                                            return -1;
                                        }
                                        else {
                                            return 0;
                                        }
                                    } )
                                }
                            }

                            return nodeData;
                        },
                        nodeData               = buildNodeDataRecursive( treeView.dataStructure );

                  if ( nodeData ) {
                      retVal.push( nodeData );
                  }
              }

              return retVal;
          },
          treeData                                  = useMemo(
              () => {
                  let retVal : ExtendTreeNode[] = [];

                  if ( treeView?.dataStructure ) {
                      nodeButtons.current = {};
                      nodeButtonPanels.current = {};

                      retVal = buildTreeData();
                  }

                  return retVal;
              },
              [
                  dataContext,
                  dataObjectsWithErrors,
                  globalDataVersions,
                  language,
                  messageCatalogTimestamp,
                  metaModelTimestamp,
                  staticTreeNodes,
                  treeView?.dataStructure
              ]
          ),
          setScrollPosition                         = () => {
              const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

              if ( scrollElement ) {
                  scrollElement.scrollLeft = scrollLeft;
                  scrollElement.scrollTop = scrollTop;
              }
          },
          getScrollPosition                         = useCallback(
              () => {
                  const scrollElement = document.getElementById( `${uid}_tree` )?.querySelector( 'ul.p-tree-container' );

                  if ( scrollElement ) {
                      dispatch( setParams( uid, {
                          scrollTop : scrollElement.scrollTop ?? 0,
                          scrollLeft: scrollElement.scrollLeft ?? 0
                      } ) );
                  }
              },
              [
                  uid
              ]
          ),
          debouncedGetScrollPosition                = useMemo(
              () => {
                  return _.debounce( getScrollPosition, 250 );
              },
              [
                  getScrollPosition
              ]
          ),
          onDoubleClick                             = useCallback(
              ( dataObjectId : number | undefined ) => {
                  if ( treeView?.nodes?.[ 0 ] && onAddTreeNode ) {
                      const newNode = onAddTreeNode(
                          treeView.nodes[ 0 ].uniqueId,
                          dataObjectId ? 'DynamicNode' : 'TreeNode',
                          dataObjectId ? dataObjectId : undefined
                      );

                      onSelectLayoutElement?.( newNode?.id );
                  }
              },
              [
                  onAddTreeNode,
                  treeView?.nodes?.[ 0 ]
              ]
          ),
          onDragStart                               = useCallback(
              ( dataObjectId : number | undefined, event : React.DragEvent<HTMLParagraphElement> ) => {
                  if ( treeView?.nodes?.[ 0 ] && onAddTreeNode && onTreeNodeDragStart ) {
                      const newNode = onAddTreeNode(
                                treeView.nodes[ 0 ].uniqueId,
                                dataObjectId ? 'DynamicNode' : 'TreeNode',
                                dataObjectId ? dataObjectId : undefined
                            ),
                            nodeKey = String( newNode?.id ?? '' );

                      onSelectLayoutElement?.( newNode?.id );

                      event.preventDefault();
                      event.stopPropagation();
                      event.nativeEvent.preventDefault();

                      requestAnimationFrame( () => {
                          onTreeNodeDragStart( nodeKey, event );
                      } );
                  }
              },
              [
                  onAddTreeNode,
                  onTreeNodeDragStart,
                  treeView?.nodes?.[ 0 ]
              ]
          ),
          onContextMenuSelectionChange              = useCallback(
              ( event : TreeSelectionEvent ) => {
                  const nodeKey = event.value
                      ? String( event.value )
                      : undefined;

                  if ( nodeKey ) {
                      setContextMenuNode( nodeKey );
                      setContextMenuModel( buildContextMenuRef.current( nodeKey ) );
                  }
                  else {
                      setContextMenuNode( undefined );
                      setContextMenuModel( [] );
                  }
              },
              []
          ),
          onContextMenu                             = useCallback(
              ( event : TreeEventNodeEvent ) => {
                  // disable context menu on static nodes
                  if ( ( event.node as ExtendTreeNode ).dataObject ) {
                      contextMenu.current?.show( event.originalEvent );
                  }
              },
              []
          ),
          treeNodeTemplate                          = useCallback(
              ( treeNodeData : PrimeTreeNode ) : React.ReactNode => {
                  const nodeData        = treeNodeData as ExtendTreeNode,
                        classNames      = ( nodeData.className ?? '' ).split( ' ' ),
                        labelClassNames = [ 'yn-label' ];

                  let retVal : React.ReactNode         = null,
                      label : string | React.ReactNode = nodeData.label ?? '';

                  // parse label
                  if ( label && typeof label === 'string' && !isInvalidHTML( label as string ) ) {
                      label = parse( label as string );
                  }

                  if ( nodeData.deleted ) {
                      classNames.push( 'yn-default-cursor' );
                      labelClassNames.push( 'yn-deleted' );
                  }

                  else if ( nodeData.error?.count ) {
                      labelClassNames.push( 'yn-error' );
                  }

                  else if ( nodeData.dirty ) {
                      labelClassNames.push( 'yn-dirty' );
                  }

                  if ( nodeData.used ) {
                      if ( !classNames.includes( 'yn-designertree-dataobject-inuse' ) ) {
                          classNames.push( 'yn-designertree-dataobject-inuse' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-designertree-dataobject-inuse' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if (
                      nodeData.dataObject
                      && (
                          nodeData.dataObject.origId === highlightedLayoutElementId
                          || ( ( flatTreeNodes.find( n => n.data.id === highlightedLayoutElementId ) as DynamicNode | undefined )?.dataObjectIds ?? [] ).includes( nodeData.dataObject.origId )
                      )
                  ) {
                      if ( !classNames.includes( 'yn-designertree-node-highlighted' ) ) {
                          classNames.push( 'yn-designertree-node-highlighted' );
                      }
                  }
                  else {
                      const index = classNames.findIndex( cM => cM === 'yn-designertree-node-highlighted' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  if (
                      nodeData.dataObject
                      && nodeData.dataObject?.origId === selectedLayoutElementId
                  ) {
                      if ( !classNames.includes( 'yn-designertree-node-selected' ) ) {
                          classNames.push( 'yn-designertree-node-selected' );
                          classNames.push( 'p-highlight' );
                      }
                  }
                  else {
                      let index = classNames.findIndex( cM => cM === 'yn-designertree-node-selected' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }

                      index = classNames.findIndex( cM => cM === 'p-highlight' );

                      if ( index !== -1 ) {
                          classNames.splice( index, 1 );
                      }
                  }

                  // build node
                  retVal = <>
                      {( !!nodeData.tooltip || ( nodeData.error?.count ?? 0 ) > 0 ) &&
                          <Tooltip
                              target={`#${nodeData.key}`}
                              showDelay={500}
                              position="top"
                              autoHide={false}
                              className="yn-tooltip"
                          >
                              {nodeData.tooltip}
                              {nodeData.error && ( nodeData.error.count ?? 0 ) > 0 &&
                                  <div key="error" className="yn-tooltip-error">
                                      <div className="yn-tooltip-header">
                                          {__( 'YN.base.common.tooltip.error.title' )}
                                      </div>
                                      {nodeData.error.tooltip}
                                  </div>
                              }
                          </Tooltip>
                      }
                      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
                      <div
                          id={`${nodeData.key}_header`}
                          className="flex flex-column flex-auto overflow-hidden yn-designertree-header"
                          draggable={!readOnly}
                          onDragStart={readOnly
                              ? undefined
                              : ( event ) => {
                                  onDragStart( nodeData.dataObject!.origId, event );
                              }}
                          onClick={() => {
                              if ( !nodeData.deleted && nodeData.selectable && !ignoreSelectionChange.current ) {
                                  onSelectLayoutElement?.( nodeData.dataObject!.origId );
                              }

                              ignoreSelectionChange.current = false;
                          }}
                          onDoubleClick={readOnly
                              ? undefined
                              : () => {
                                  onDoubleClick( nodeData.dataObject?.origId );
                              }}
                          onMouseEnter={() => {
                              onHighlightLayoutElement?.( nodeData.dataObject?.origId );
                          }}
                          onMouseLeave={() => {
                              onHighlightLayoutElement?.();
                          }}
                      >
                          <div id={`${nodeData.key}`} className="yn-treenode-header">
                              <span key="label" className={labelClassNames.join( ' ' )}>{label}</span>
                              {nodeData.buttons}
                          </div>
                      </div>
                  </>;

                  nodeData.className = [ ...classNames ].join( ' ' );

                  return retVal;
              },
              [
                  highlightedLayoutElementId,
                  language,
                  messageCatalogTimestamp,
                  flatTreeNodes,
                  onDoubleClick,
                  onDragStart,
                  onHighlightLayoutElement,
                  onSelectLayoutElement,
                  readOnly,
                  selectedLayoutElementId
              ]
          ),
          treeTogglerTemplate                       = useCallback(
              () : React.ReactNode => {
                  return <span className="yn-tree-toggler-placeholder"></span>;
              },
              []
          ),
          content                                   = useMemo(
              () => {
                  let retVal = <></>;

                  if ( treeView?.dataStructure ) {
                      retVal = <>
                          <ContextMenu
                              key={`${uid}_contextMenu`}
                              model={contextMenuModel}
                              ref={el => contextMenu.current = el}
                              onHide={() => setContextMenuNode( undefined )}
                              pt={{
                                  root: {
                                      style: {
                                          minWidth: '15rem'
                                      }
                                  }
                              }}
                          />
                          <Tree
                              key={`${uid}_tree`}
                              id={`${uid}_tree`}

                              ref={el => treeElement.current = el?.getElement() ?? null}

                              value={treeData}
                              nodeTemplate={treeNodeTemplate}

                              expandedKeys={expandedKeys}
                              togglerTemplate={treeTogglerTemplate}
                              // dummy on toggle needed to force PrimeReact tree to obey expanded keys property
                              onToggle={() => {
                              }}

                              className="yn-menu-tree yn-node-content-tree yn-designertree"
                              dragdropScope="treedesigner"

                              contextMenuSelectionKey={contextMenuNode}
                              onContextMenuSelectionChange={onContextMenuSelectionChange}
                              onContextMenu={onContextMenu}

                              pt={{
                                  container: {
                                      onScroll: debouncedGetScrollPosition
                                  },
                                  footer   : {
                                      style: {
                                          margin: '1rem -1rem -1rem -1rem',
                                          width : 'calc(100% + 2rem)',
                                          zIndex: '10'
                                      }
                                  }
                              }}
                          />
                      </>;
                  }

                  return retVal;
              },
              [
                  contextMenuModel,
                  contextMenuNode,
                  expandedKeys,
                  onContextMenu,
                  onContextMenuSelectionChange,
                  rootItemId,
                  treeData,
                  treeNodeTemplate,
                  treeTogglerTemplate,
                  treeView?.dataStructure,
                  uid
              ]
          );

    buildContextMenuRef.current = buildContextMenu;

    // expose methods via reference
    useImperativeHandle(
        forwardedRef,
        () => ( {} )
    );

    // sync expanded keys after tree data changes
    useEffect(
        () => {
            if ( treeData?.length ) {
                const newExpandedKeys : TreeTableExpandedKeysType = {},
                      collectKeys                                 = ( nodes : ExtendTreeNode[] ) => {
                          for ( const node of nodes ) {
                              newExpandedKeys[ node.key ] = true;

                              if ( node.children?.length ) {
                                  collectKeys( node.children );
                              }
                          }
                      };

                collectKeys( treeData );

                if ( Object.keys( newExpandedKeys ).length ) {
                    dispatch( setParams( uid, {
                        expandedKeys: newExpandedKeys
                    } ) );
                }
            }
        },
        [
            treeData
        ]
    );

    // set scroll position (on initial render or change)
    useLayoutEffect(
        () => {
            setScrollPosition();
        },
        [
            scrollLeft,
            scrollTop
        ]
    );

    return content;
} );
DesignerTree.defaultProps = defaultProps;
DesignerTree.displayName = 'TreeDesigner-DesignerTree';


export default DesignerTree;
